'use client';
import { faChevronRight, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from '@/components/ui';
import { modalMotionFromRightToLeft } from '@/constants/constants';
import useCoreConfig from '@/hooks/use-core-config';
import { usePriceFilterContext } from '@/hooks/use-price-filter-context';
import useProductListingSearchParams from '@/hooks/use-product-listing-search-params';
import useProductsPageContext from '@/hooks/use-products-page-context';
import { useAppContext, useTranslation } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import {
  useStudioPreviewPortalRoot,
  useStudioPreviewScrollLock,
} from '@/providers/studio-preview-portal-provider';
import { Filter } from '@/types/product-filter';
import { cn } from '@/utils';
import { createFilters } from '@/utils/create-filters';
import { getPriceFilter } from '@/utils/get-price-filter';
import {
  clearProductListingFilterParams,
  getProductListingFilterEntries,
} from '@/utils/product-listing-search-params';
import { studioPreviewPortalModalClassNames } from '@/utils/studio-preview-portal-modal';
import ProductFilter from '../product-filter/product-filter';
import SelectedFilterChip from './selected-filter-chip/selected-filter-chip';

const MobileFilters = () => {
  const { t } = useTranslation();
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>() ?? {};
  const portalRoot = useStudioPreviewPortalRoot();
  const constrainModalToStudioPortal = portalRoot != null;
  const { category, products, categoryFilters: serverCategoryFilters } = useProductsPageContext();
  const priceFilterCtx = usePriceFilterContext();
  const { isAuthenticated } = useKeycloak();

  const router = useRouter();
  const searchParams = useProductListingSearchParams();
  const listingSearchParamsOptions = { isStudioPreview: Boolean(isStudioPreview) };

  const dynamicFilters = useMemo(
    () => serverCategoryFilters ?? createFilters(category),
    [serverCategoryFilters, category],
  );
  const { currency, features } = useCoreConfig();

  const hasPriceFilter =
    features.priceFilter && (!features.priceForGuests ? isAuthenticated : true);
  const priceFilter = useMemo(
    () => getPriceFilter(products.items, currency, t, priceFilterCtx),
    [products.items, currency, t, priceFilterCtx],
  );
  const filters = useMemo(
    () => (hasPriceFilter ? [priceFilter, ...dynamicFilters] : dynamicFilters),
    [hasPriceFilter, priceFilter, dynamicFilters],
  );
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [selectedFilter, setSelectedFilter] = useState<Filter | undefined>(undefined);
  const {
    isOpen: isDetailOpen,
    onOpen: onDetailOpen,
    onOpenChange: onDetailOpenChange,
  } = useDisclosure();

  useStudioPreviewScrollLock((isOpen || isDetailOpen) && constrainModalToStudioPortal);

  // Derived, not state: as state it looped (unstable object in the effect deps).
  const activeFilters = useMemo(
    () =>
      getProductListingFilterEntries(searchParams, {
        isStudioPreview: Boolean(isStudioPreview),
      }).map(([key, value]) => ({
        label: key.charAt(0).toUpperCase() + key.slice(1),
        value,
      })),
    [searchParams, isStudioPreview],
  );

  const handleFilterClick = (filter: Filter) => {
    setSelectedFilter(filter);
    onDetailOpen();
  };

  const handleRemoveFilter = (label: string, value: string) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    const key = label.charAt(0).toLowerCase() + label.slice(1);
    const values = newSearchParams.getAll(key).filter(val => val !== value);
    newSearchParams.delete(key);
    values.forEach(val => newSearchParams.append(key, val));
    router.push(`?${newSearchParams.toString()}`);
  };

  const handleClearAll = () => {
    const newSearchParams = clearProductListingFilterParams(
      searchParams,
      listingSearchParamsOptions,
    );
    router.push(`?${newSearchParams.toString()}`);
  };

  const totalValuesCount = activeFilters.length;

  return (
    <>
      <Badge content={totalValuesCount} data-testid="mobile-filters__badge">
        <Button onClick={onOpen} data-testid="mobile-filters__open-button" variant="flat">
          {t?.products.filters}
        </Button>
      </Badge>
      <Modal
        aria-labelledby="modal-title"
        isOpen={isOpen}
        hideCloseButton
        size="full"
        portalContainer={portalRoot ?? undefined}
        classNames={constrainModalToStudioPortal ? studioPreviewPortalModalClassNames : undefined}
      >
        <ModalContent
          className={cn(
            'bg-background flex min-h-0 flex-col',
            constrainModalToStudioPortal && 'h-full max-h-full',
          )}
        >
          <ModalHeader className="flex items-center justify-between pr-10">
            <p data-testid="mobile-filters__modal-title">{t?.products.filters}</p>
            <div className="flex items-center gap-6">
              <Button
                variant="bordered"
                className="rounded-full"
                onClick={handleClearAll}
                data-testid="mobile-filters__clear-all"
              >
                {t?.products.deleteFilters}
              </Button>
              <IconButton
                aria-label="Close filters"
                onClick={onOpenChange}
                className="bg-transparent"
                data-testid="mobile-filters__close-button"
              >
                <Icon icon={faXmark} size="lg" />
              </IconButton>
            </div>
          </ModalHeader>
          <ModalBody
            className={cn(
              'min-h-0 flex-1',
              constrainModalToStudioPortal ? 'max-h-full' : 'max-h-[80dvh]',
            )}
          >
            <div className="h-full overflow-y-auto pr-2">
              {activeFilters.map((filter, index) => (
                <SelectedFilterChip
                  key={`${filter.label}-${filter.value}` || index}
                  filterName={filter.label}
                  selectedFilter={filter}
                  onRemove={() => handleRemoveFilter(filter.label, filter.value)}
                  data-testid={`mobile-filters__selected-chip-${index}`}
                />
              ))}
              {filters.map(filter => (
                <Button
                  key={filter.id}
                  onClick={() => handleFilterClick(filter)}
                  className="mb-2 flex w-full justify-between pr-6"
                  endContent={<Icon icon={faChevronRight} />}
                  data-testid={`mobile-filters__filter-button-${filter.id}`}
                  variant="flat"
                >
                  {filter.name}
                </Button>
              ))}
            </div>
          </ModalBody>
          <ModalFooter className="flex justify-center">
            <Button
              color="primary"
              className="w-4/5 text-white"
              onClick={onOpenChange}
              data-testid="mobile-filters__apply-filters"
            >
              {t?.products.applyFilters}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        aria-labelledby="modal-title"
        isOpen={isDetailOpen}
        onClose={onDetailOpenChange}
        hideCloseButton
        size="full"
        motionProps={modalMotionFromRightToLeft}
        portalContainer={portalRoot ?? undefined}
        classNames={constrainModalToStudioPortal ? studioPreviewPortalModalClassNames : undefined}
      >
        <ModalContent
          className={cn(
            'bg-background flex min-h-0 flex-col',
            constrainModalToStudioPortal && 'h-full max-h-full',
          )}
        >
          <ModalHeader className="flex items-center justify-between pr-4">
            <p data-testid="mobile-filters__detail-title">{selectedFilter?.name}</p>
          </ModalHeader>
          <ModalBody
            className={cn(
              'min-h-0 flex-1',
              constrainModalToStudioPortal ? 'max-h-full' : 'max-h-[80dvh]',
            )}
          >
            {selectedFilter && (
              <ProductFilter filter={selectedFilter} data-testid="mobile-filters__product-filter" />
            )}
          </ModalBody>
          <ModalFooter className="flex justify-center">
            <Button
              color="primary"
              className="w-4/5 text-white"
              onClick={onDetailOpenChange}
              data-testid="mobile-filters__back-button"
            >
              {t?.products.back}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default MobileFilters;
